import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostBinding, computed, effect, inject, input, signal } from '@angular/core';

import { TotalsPrefix } from '@cds/betting-offer';
import { ScoreboardFactory } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { EventOption, EventOptionGroup, EventParticipant, SpecialChar } from '@frontend/sports/betting-offer/feature/model';
import { OfferGroupingService } from '@frontend/sports/betting-offer/feature/offer-grouping';
import { ShortNameModule, ShortTeamNamesService } from '@frontend/sports/betting-offer/feature/short-team-names';
import { LiveSectionConfig, Sitecore, SitecoreGameMessagesConfig } from '@frontend/sports/common/client-config-data-access';
import { NativePriceFormatterPipe } from '@frontend/sports/odds/feature/native-price';
import { ParticipantImageComponent } from '@frontend/sports/participant-image/feature';
import { DsDivider } from '@frontend/ui/divider';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { filter, map } from 'rxjs';

import { EventDetailsService } from '../../event-details-common/event-details.service';
import { OptionGroupService } from '../../event-model/helpers/option-group.service';
import { GridScoreboardComponent } from '../../grid-scoreboard/components/grid-scoreboard.component';
import { GridEventScoreboardHeaderComponent } from '../../grid-scoreboard/components/scoreboard-headers/grid-scoreboard-header.component';
import { OptionIndicatorDirective } from '../../option-pick/option-indicator.directive';
import { EventTimerComponent } from '../../timers/event-timer.component';
import { EventCarouselItem } from '../event-carousel.model';
import { EventDetailCardHeaderComponent } from './event-detail-card-header/event-detail-card-header.component';

interface GridableMarketInfo {
    lineValue?: string;
    isOnline: boolean;
    option: EventOption;
}

@Component({
    selector: 'ms-event-detail-card',
    templateUrl: './event-detail-card.component.html',
    styleUrl: './event-detail-card.component.scss',
    imports: [
        ParticipantImageComponent,
        IconCustomComponent,
        OptionIndicatorDirective,
        EventDetailCardHeaderComponent,
        NativePriceFormatterPipe,
        DsDivider,
        AsyncPipe,
        GridScoreboardComponent,
        NgTemplateOutlet,
        EventTimerComponent,
        GridEventScoreboardHeaderComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventDetailCardComponent {
    scoreboardType = input<'small' | 'large'>('small');
    isLargeScoreboard = computed(() => this.scoreboardType() === 'large');
    isSmallScoreboard = computed(() => this.scoreboardType() === 'small');
    private shortTeamNamesService = inject(ShortTeamNamesService);
    private scoreboardFactory = inject(ScoreboardFactory);
    private liveSectionConfig = inject(LiveSectionConfig);
    private eventDetailsService = inject(EventDetailsService);
    private optionGroupService = inject(OptionGroupService);
    private grouping = inject(OfferGroupingService);
    private sitecore = inject(Sitecore);
    private sitecoreGameMessage = inject(SitecoreGameMessagesConfig);
    @HostBinding('class') class = 'event-detail-card';
    broadcaster$ = this.sitecoreGameMessage.whenReady.pipe(
        map(() => this.eventCarouselItem()),
        filter((item) => !item.event.scoreboard?.isFinished && item.event.scoreboard?.started),
        map((item) => (this.sitecoreGameMessage.secondaryBroadcastInfo?.messages ?? {})[item.event.id]),
    );
    eventCarouselItem = input.required<EventCarouselItem>();
    gridableMarketInfo = signal<(GridableMarketInfo | undefined)[]>([]);

    constructor() {
        effect(() => {
            this.setGridableMarketInfo();
        });
    }

    liveEventInfo = computed(() =>
        this.getLiveEventInfo(this.eventCarouselItem().event.sport.id, this.eventCarouselItem().event.league.parentLeagueId),
    );

    participantInfos = computed(() =>
        this.eventDetailsService.setParticipantInfos(this.eventCarouselItem().event).map((participantInfo) => {
            participantInfo.participant.name = this.getParticipantName(participantInfo.participant);
            participantInfo.isSingleContestant = !participantInfo.participant.name.includes(SpecialChar.Slash);
            return participantInfo;
        }),
    );

    private getParticipantName(participant: EventParticipant): string {
        const shouldUseShortName = this.shortTeamNamesService.shouldUseShortName(
            participant,
            this.eventCarouselItem().event.sport.id,
            this.eventCarouselItem().event.league.id,
            ShortNameModule.Grids,
        );

        if (shouldUseShortName) {
            return this.shortTeamNamesService.clearShortName(participant.shortName!);
        }

        return participant.name;
    }

    private getLiveEventInfo(sportsId: number, leagueId?: number): string | undefined {
        const scoreboard = this.eventCarouselItem().event.scoreboard;
        if (!scoreboard.started || !leagueId || !this.liveSectionConfig.liveEventsInfoForLeagueIds.includes(leagueId)) {
            return;
        }

        return this.scoreboardFactory.getSportsLiveEventInfo(sportsId, scoreboard);
    }

    private setGridableMarketInfo(): void {
        this.grouping.getGridGrouping(this.eventCarouselItem().event.sport.id).subscribe((config) => {
            if (!config) {
                return;
            }

            const sixPackConfig = config.sixPackGroups.find((group) => group.marketGroupIds.length > 0);
            if (sixPackConfig) {
                this.gridableMarketInfo.set(this.processEventOptions(sixPackConfig.marketGroupIds));

                return;
            }

            const marketGroup = config.groups.find((group) => !group.isFallbackGroup);
            if (marketGroup) {
                this.gridableMarketInfo.set(this.processMatchResult(marketGroup.id));
            }
        });
    }

    private processMatchResult(marketGroupId: string): (GridableMarketInfo | undefined)[] {
        const eventOptionGroup = this.getOrderedOptionGroup(marketGroupId);
        if (!eventOptionGroup) {
            return [];
        }

        // we need to show odds related to home and away team only, so skipping tie option for 3way market
        const options: EventOption[] =
            eventOptionGroup.options.length > 2 ? [eventOptionGroup.options[0], eventOptionGroup.options[2]] : eventOptionGroup.options;

        return options.map((option) => this.convertToGridableInfo(eventOptionGroup.online, option));
    }

    private processEventOptions(marketGroupIds: string[]): (GridableMarketInfo | undefined)[] {
        let totalOption: GridableMarketInfo | undefined;
        let spreadOption: GridableMarketInfo | undefined;
        let spreadOptionPosition = 0;

        let moneyLineOptions: GridableMarketInfo[] = [];

        for (const id of marketGroupIds) {
            const eventOptionGroup = this.getOrderedOptionGroup(id);
            if (!eventOptionGroup) {
                continue;
            }

            const spreadOptions = eventOptionGroup.options.filter((option) => option.optionAttribute && !option.totalsPrefix);
            if (spreadOptions.length) {
                const { gridableMarketInfo, optionPosition } = this.getSpreadValueFromSpreadOptions(eventOptionGroup);
                spreadOption = gridableMarketInfo;
                spreadOptionPosition = optionPosition;

                continue;
            }

            const totalOptions = eventOptionGroup.options.filter((option) => option.marketAttribute && option.totalsPrefix === TotalsPrefix.Over);
            if (totalOptions.length) {
                totalOption = this.convertToGridableInfo(
                    eventOptionGroup.online,
                    totalOptions[0],
                    this.getOverValueFromTotalOptions(totalOptions[0]),
                );

                continue;
            }

            if (!moneyLineOptions.length && !(spreadOption && totalOption)) {
                moneyLineOptions = eventOptionGroup.options.map((option) => this.convertToGridableInfo(eventOptionGroup.online, option));
            }
        }

        if (spreadOption && totalOption) {
            return spreadOptionPosition == 0 ? [spreadOption, totalOption] : [totalOption, spreadOption];
        }

        return moneyLineOptions;
    }

    private getOrderedOptionGroup(marketGroupId: string): EventOptionGroup | undefined {
        const optionGroup = this.optionGroupService.get(
            this.eventCarouselItem().event.optionGroups,
            this.eventCarouselItem().event.sport.id,
            this.eventCarouselItem().event.live,
            marketGroupId,
        );

        return this.optionGroupService.orderBasedOnParticipants(
            optionGroup,
            this.eventCarouselItem().event.participants,
            this.eventCarouselItem().event.viewType,
        );
    }

    private getOverValueFromTotalOptions(totalOption: EventOption): string {
        return `${totalOption.totalsPrefix === TotalsPrefix.Over ? this.sitecore.eventGrid.SixPack_TotalsOver : ''} ${totalOption.marketAttribute}`;
    }

    private getSpreadValueFromSpreadOptions(eventOptionGroup: EventOptionGroup): {
        gridableMarketInfo: GridableMarketInfo | undefined;
        optionPosition: number;
    } {
        let gridableMarketInfo: GridableMarketInfo | undefined;
        let optionPosition = 0;

        eventOptionGroup.options.forEach((spreadOption, index) => {
            const optionName = spreadOption.optionAttribute;
            if (optionName?.startsWith('-')) {
                gridableMarketInfo = this.convertToGridableInfo(eventOptionGroup.online, spreadOption, optionName);
                optionPosition = index;
            }
        });

        return { gridableMarketInfo, optionPosition };
    }

    private convertToGridableInfo(optionGroupOnline: boolean, eventOption: EventOption, value?: string): GridableMarketInfo {
        return {
            lineValue: value,
            isOnline: optionGroupOnline && eventOption.online,
            option: eventOption,
        };
    }
}
